import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import { Heart, ArrowLeft, Plus, TrendingUp, Trash2, Sparkles, Eye, EyeOff, Share2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const categoryColors = {
  'communication': 'bg-blue-100 text-blue-700 border-blue-200',
  'play': 'bg-green-100 text-green-700 border-green-200',
  'regulation': 'bg-purple-100 text-purple-700 border-purple-200',
  'social connection': 'bg-pink-100 text-pink-700 border-pink-200'
};

const categoryIcons = {
  'communication': '💬',
  'play': '🎨',
  'regulation': '🌊',
  'social connection': '🤝'
};

export default function ProgressTracking({ onLogout }) {
  const navigate = useNavigate();
  const { childId } = useParams();
  const [progress, setProgress] = useState([]);
  const [child, setChild] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({
    date: '',
    observation: '',
    category: 'communication',
    shared_with_parent: false
  });
  const user = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    fetchData();
  }, [childId]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [childRes, progressRes] = await Promise.all([
        axios.get(`${API}/children/${childId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/progress/${childId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setChild(childRes.data);
      setProgress(progressRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/progress`,
        { ...formData, child_id: childId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Progress note added!');
      setShowDialog(false);
      fetchData();
      setFormData({
        date: '',
        observation: '',
        category: 'communication',
        shared_with_parent: false
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add progress note');
    }
  };

  const handleToggleShare = async (progressId, currentState) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/progress/${progressId}/share`,
        { shared_with_parent: !currentState },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(!currentState ? 'Note shared with parent' : 'Note hidden from parent');
      fetchData();
    } catch (error) {
      toast.error('Failed to update sharing');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this progress note?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/progress/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Progress note deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete progress note');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <p className="text-stone-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              data-testid="back-btn"
              onClick={() => navigate(`/children/${childId}`)}
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Heart className="w-5 h-5 text-primary" />
              </div>
              <h1 className="text-2xl font-serif text-primary-900">PlayFirst</h1>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-4xl md:text-5xl font-serif font-normal text-primary-900 mb-2">
                {child?.name}'s journey
              </h2>
              <p className="text-lg text-stone-600">Strengths & growth moments</p>
            </div>
            {user.role === 'therapist' && (
              <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="add-progress-btn" className="rounded-full shadow-lg shadow-primary/20">
                    <Plus className="w-4 h-4 mr-2" />
                    Add note
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-3xl max-w-2xl" data-testid="progress-dialog">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-serif">Add progress note</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreate} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="date">Date</Label>
                      <Input
                        id="date"
                        data-testid="date-input"
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        required
                        className="h-12 rounded-2xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="category">Category</Label>
                      <Select
                        value={formData.category}
                        onValueChange={(value) => setFormData({ ...formData, category: value })}
                      >
                        <SelectTrigger data-testid="category-select" className="h-12 rounded-2xl">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="communication">💬 Communication</SelectItem>
                          <SelectItem value="play">🎨 Play</SelectItem>
                          <SelectItem value="regulation">🌊 Regulation</SelectItem>
                          <SelectItem value="social connection">🤝 Social Connection</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="observation">Observation (strengths-based)</Label>
                      <Textarea
                        id="observation"
                        data-testid="observation-input"
                        placeholder="Initiated turn-taking during bubble play today..."
                        value={formData.observation}
                        onChange={(e) => setFormData({ ...formData, observation: e.target.value })}
                        required
                        className="rounded-2xl min-h-32"
                      />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-stone-50 rounded-2xl">
                      <div className="flex items-center gap-3">
                        <Share2 className="w-5 h-5 text-stone-500" />
                        <div>
                          <Label htmlFor="share-toggle" className="text-sm font-medium cursor-pointer">
                            Share with parent
                          </Label>
                          <p className="text-xs text-stone-500">Parent will see this note</p>
                        </div>
                      </div>
                      <Switch
                        id="share-toggle"
                        data-testid="share-toggle"
                        checked={formData.shared_with_parent}
                        onCheckedChange={(checked) => setFormData({ ...formData, shared_with_parent: checked })}
                      />
                    </div>
                    <Button type="submit" data-testid="create-progress-btn" className="w-full h-12 rounded-full">
                      Add note
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          {user.role === 'parent' && (
            <div className="mb-8 p-6 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-3xl">
              <p className="font-accent text-xl text-center text-primary-800">
                Celebrate every step forward — growth happens in the small moments!
              </p>
            </div>
          )}

          {progress.length === 0 ? (
            <Card className="p-12 text-center bg-white rounded-3xl border border-stone-100" data-testid="empty-state">
              <TrendingUp className="w-16 h-16 mx-auto mb-4 text-primary/50" />
              <h3 className="text-xl font-medium text-stone-700 mb-2">No progress notes yet</h3>
              <p className="text-stone-500 mb-6">
                {user.role === 'therapist'
                  ? 'Start documenting growth moments and strengths'
                  : 'Your therapist will add progress observations here'}
              </p>
            </Card>
          ) : (
            <div className="space-y-6">
              {progress.map((note, index) => {
                const date = new Date(note.date);
                const monthYear = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                const day = date.getDate();
                
                return (
                  <motion.div
                    key={note.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                  >
                    <Card className="p-6 bg-white rounded-3xl border border-stone-100 hover:shadow-md transition-all relative" data-testid={`progress-card-${note.id}`}>
                      <div className="flex gap-6">
                        <div className="flex-shrink-0 text-center">
                          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex flex-col items-center justify-center">
                            <div className="text-2xl font-bold text-primary">{day}</div>
                            <div className="text-xs text-stone-500">{monthYear}</div>
                          </div>
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${categoryColors[note.category]}`}>
                                {categoryIcons[note.category]} {note.category}
                              </span>
                              {user.role === 'therapist' && (
                                <button
                                  onClick={() => handleToggleShare(note.id, note.shared_with_parent)}
                                  data-testid={`share-btn-${note.id}`}
                                  className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                    note.shared_with_parent 
                                      ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                                      : 'bg-stone-100 text-stone-500 hover:bg-stone-200'
                                  }`}
                                  title={note.shared_with_parent ? 'Visible to parent - click to hide' : 'Hidden from parent - click to share'}
                                >
                                  {note.shared_with_parent ? (
                                    <><Eye className="w-3.5 h-3.5" /> Shared</>
                                  ) : (
                                    <><EyeOff className="w-3.5 h-3.5" /> Private</>
                                  )}
                                </button>
                              )}
                            </div>
                            {user.role === 'therapist' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                data-testid={`delete-progress-btn-${note.id}`}
                                onClick={() => handleDelete(note.id)}
                                className="rounded-full h-8 w-8 p-0 text-destructive hover:text-destructive"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                          <p className="text-base text-stone-700 leading-relaxed">{note.observation}</p>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
}
